/**
 * Public landing page — what a visitor sees before signing in.
 *
 * Structure follows the prototype's `#screen-landing` exactly: overlay nav, gradient
 * hero with a glass course-preview panel, values, a real course grid, and a closing CTA
 * banner.
 *
 * Two departures from the prototype, both deliberate:
 *
 *  - The course grid uses REAL courses from `/api/courses/v1/courses/` rather than the
 *    prototype's demo data. A landing page that lies about the catalogue is worse than
 *    one that shows three courses.
 *  - Every string comes from the tenant's copy catalogue, so each academy writes its
 *    own hero without a code change. The prototype's Mereka copy is the fallback.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Banner, Button } from '../../shared/ui';
import { getConfig, isEnabled, text } from '../../shared/lib/config';
import { openedx } from '../../shared/lib/openedx';
import { coursePriceView } from '../../shared/lib/pricing';
import { useCoursePrices } from '../../shared/lib/usePricing';
import { PriceTag } from '../../shared/ui/price';
import './landing.css';

interface CourseSummary {
  id: string;
  name: string;
  org: string;
  short_description: string | null;
  media?: { image?: { small?: string } };
}

interface CourseList {
  results: CourseSummary[];
}

export default function Landing() {
  const config = getConfig();

  // `isError` is destructured, and that is the whole point of this query.
  //
  // This is the UNAUTHENTICATED FRONT DOOR. Discarding the error here does not degrade the
  // page, it makes it lie: a failed read leaves `courses` empty, and the catalogue section
  // below then tells a first-time visitor "No courses are published yet" about an academy
  // that is full of them. A 429 from the platform's course-list throttle produced exactly
  // that, and nothing on the page or in the browser console said otherwise.
  //
  // `retry: false` stays. This request is most likely to fail when the platform is already
  // over capacity, and retrying would add load in precisely that moment. The visitor gets an
  // explicit "Try again" instead, so the retry is a decision rather than a reflex.
  //
  // `isPending` is destructured for the same reason as `isError`, and missing it is the same
  // bug wearing a different hat: while the first fetch is in flight there is no error and no
  // data, so an empty-state check on `courses.length` alone puts "No courses are published
  // yet" in front of every visitor for the length of the request, on every cold load, and
  // then swaps it for the grid. The claim is false for a shorter time, not less false.
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['landing-courses'],
    // Anonymous read: the catalogue must render for visitors, so this endpoint is
    // called without a session and scoped by the tenant's org filter server side.
    queryFn: () => openedx.get<CourseList>('/api/courses/v1/courses/?page_size=6'),
    retry: false,
  });

  const courses = data?.results ?? [];

  /**
   * PRICES FOR THE SIX CARDS, READ FROM THE SEAM DIRECTLY (spec R6.5).
   *
   * This page is the one course surface that does NOT go through `listCatalogue`: it has its
   * own `CourseSummary` above and its own anonymous query, for the reasons the header
   * explains. So it asks the pricing seam itself rather than inheriting a price from a
   * catalogue helper it never calls — the alternative would be adding a price field to
   * `CourseSummary` and a second place that decides what a missing one means.
   *
   * At most six keys, from the same `page_size=6` read above, so this is one small request
   * on the unauthenticated front door and it shares its cache entry with Discover whenever
   * the same six courses lead that grid.
   */
  const courseIds = useMemo(() => courses.map((course) => course.id), [courses]);
  const prices = useCoursePrices(courseIds);

  // Platform defaults merged with this tenant's overrides — editable, not hardcoded.
  const values = config.landing?.values ?? [];
  const stats = config.landing?.stats ?? [];

  return (
    <div className="landing">
      <div className="landing__hero">
        <nav className="landing__nav" aria-label="Main">
          <Link to="/" className="landing__wordmark">
            {config.tenant.name}
          </Link>

          <div className="landing__nav-actions">
            {isEnabled('learning_paths') && (
              <Link to="/paths" className="landing__nav-link">
                {text('nav_paths', 'Paths')}
              </Link>
            )}
            <Link to="/login" className="landing__nav-link">
              {text('sign_in', 'Sign in')}
            </Link>
            {config.auth.allowNativeRegistration && (
              <Link to="/register" className="ui-btn" data-variant="primary">
                Get started
              </Link>
            )}
          </div>
        </nav>

        <div className="landing__hero-inner">
          <div className="landing__hero-text">
            <h1>{text('hero_heading', 'Build the skills that move you forward.')}</h1>
            <p>
              {text(
                'hero_body',
                'Industry-led courses, hands-on projects, and mentors who have built what you are learning.',
              )}
            </p>
            <div className="landing__hero-cta">
              <Link to="/register" className="ui-btn" data-variant="primary">
                {text('hero_cta', 'Start learning free')}
              </Link>
              <a href="#courses" className="ui-btn" data-variant="secondary">
                {text('hero_cta_secondary', 'Browse courses')}
              </a>
            </div>

            {stats.length > 0 && (
              <div className="landing__hero-stats">
                {stats.map((stat) => (
                  <div className="landing__hero-stat" key={stat.label}>
                    <b>{stat.value}</b>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Glass panel. Shows real courses when they exist; when the catalogue is
              empty — or could not be read, or has not answered yet — it is omitted entirely
              rather than rendering an empty frame. Omitting is not the same as asserting:
              the panel makes no claim about the academy either way, so it needs no error
              branch of its own. The catalogue section below is where the failure is
              reported, in words, once. */}
          {courses.length > 0 && (
            <div className="landing__hero-preview">
              <h3>Popular right now</h3>
              {courses.slice(0, 3).map((course, index) => (
                <Link
                  key={course.id}
                  to={`/courses/${encodeURIComponent(course.id)}`}
                  className="landing__preview-card"
                >
                  <span className="landing__preview-index">{`0${index + 1}`}</span>
                  <span>
                    <strong>{course.name}</strong>
                    <span>{course.org}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {values.length > 0 && (
        <section className="landing__section landing__section--centred">
          <p className="landing__eyebrow">Why {config.tenant.name}</p>
          <h2>{text('values_heading', 'Learning that holds up at work')}</h2>
          <p className="landing__lede">{text('values_subheading', '')}</p>
          <div className="landing__values-grid">
            {values.map((value) => (
              <div className="landing__value-card" key={value.title}>
                {value.icon && (
                  <div className="landing__value-icon" aria-hidden="true">
                    <span className="material-symbols-outlined">{value.icon}</span>
                  </div>
                )}
                <h3>{value.title}</h3>
                <p>{value.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="landing__section landing__section--tinted" id="courses">
        <div>
          <p className="landing__eyebrow">Catalogue</p>
          <h2>{text('catalogue_heading', 'Start with one of these')}</h2>

          {/* BOTH UNSETTLED BRANCHES COME FIRST, and the order is the fix. "No courses are
              published yet" is a statement about the academy; it may only be made once the
              academy has actually answered — which rules out the failed read AND the read
              still in flight. Same order as Discover, which had this right already.

              The skeleton's classes come from learner.css rather than this file, the way
              `ui-btn` above comes from ui.css: Vite emits one stylesheet for the app, so
              these are shared primitives, not a cross-route import. */}
          {isPending ? (
            <div className="landing__courses-grid" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div className="landing__course-card landing__course-card--skeleton" key={index}>
                  <div className="landing__course-thumb" />
                  <div className="landing__course-body">
                    <span className="skeleton skeleton--title" />
                    <span className="skeleton skeleton--line" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <Banner tone="danger">
              <strong>The catalogue could not be loaded.</strong> The academy did not answer.
              This is not something you have done — nothing has changed, so retrying is safe.
              <div className="landing__banner-action">
                <Button variant="secondary" onClick={() => void refetch()}>
                  Try again
                </Button>
              </div>
            </Banner>
          ) : courses.length === 0 ? (
            <p style={{ color: 'var(--ink-muted)' }}>
              No courses are published yet. They will appear here as soon as they are.
            </p>
          ) : (
            <>
              <div className="landing__courses-grid">
                {courses.map((course) => (
                  <Link
                    key={course.id}
                    to={`/courses/${encodeURIComponent(course.id)}`}
                    className="landing__course-card"
                  >
                    <div className="landing__course-thumb" aria-hidden="true">
                      {course.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="landing__course-body">
                      <span className="landing__chip">{course.org}</span>
                      {/*
                        THE PRICE CHIP, AND THE ONE PLACE "PRICE UNAVAILABLE" IS OMITTED
                        RATHER THAN STATED (spec R6.5).

                        `omitUnknown` is safe HERE AND ONLY HERE, and the reason is that this
                        page has no buy control to mislabel: every card is a link to the
                        course page, which does state the reason (R6.4). On a marketing grid
                        of three-line cards, "Price unavailable" is noise in front of a reader
                        who has not yet asked what anything costs. A FREE or PAID price is
                        still shown, so omission only ever hides a fact this page could not
                        establish — never one it could.
                      */}
                      <PriceTag view={coursePriceView(prices, course.id)} omitUnknown />
                      <h4>{course.name}</h4>
                      {course.short_description && <p>{course.short_description}</p>}
                    </div>
                  </Link>
                ))}
              </div>

              <div className="landing__courses-more">
                <Link to="/discover" className="ui-btn">
                  See all courses
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="landing__cta-banner">
        <h2>{text('cta_heading', 'Ready to start?')}</h2>
        <p>{text('cta_body', '')}</p>
        <Link to="/register" className="ui-btn" data-variant="primary">
          {text('hero_cta', 'Start learning free')}
        </Link>
      </section>

      <footer className="landing__footer">
        <span className="landing__wordmark" style={{ color: 'var(--color-link)' }}>
          {config.tenant.name}
        </span>
        <nav aria-label="Footer">
          <a href="/about">About</a>
          <a href="/tos">Terms</a>
          <a href="/privacy">Privacy</a>
        </nav>
      </footer>
    </div>
  );
}
